import React, { useState, useEffect, useCallback } from 'react';
import { Location } from '../types/core';
import { announceToScreenReader, generateId, handleKeyboardActivation } from '../utils/accessibility';
import { Button } from './Button';
import {
  highAccuracyLocationService,
  checkiOSLocationAccuracy,
  getBrowserOptimizedOptions,
  type LocationProgress,
  type LocationResult
} from '../utils/high-accuracy-location';

interface LocationInputProps {
  onLocationChange: (location: Location | null) => void;
  onError?: (error: LocationError) => void;
  disabled?: boolean;
  className?: string;
}

export interface LocationError {
  code: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'NOT_SUPPORTED';
  message: string;
}

type LocationState = 'idle' | 'requesting' | 'success' | 'error';
type PermissionState = 'prompt' | 'granted' | 'denied' | 'unknown';

export function LocationInput({
  onLocationChange,
  onError,
  disabled = false,
  className = ''
}: LocationInputProps) {
  const [locationState, setLocationState] = useState<LocationState>('idle');
  const [permissionState, setPermissionState] = useState<PermissionState>('unknown');
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [error, setError] = useState<LocationError | null>(null);
  const [locationProgress, setLocationProgress] = useState<LocationProgress | null>(null);
  const [locationResult, setLocationResult] = useState<LocationResult | null>(null);
  const [iosAccuracyMode, setIosAccuracyMode] = useState<'precise' | 'reduced' | 'unknown'>('unknown');

  // Generate unique IDs for accessibility
  const buttonId = generateId('location-button');
  const statusId = generateId('location-status');
  const errorId = generateId('location-error');

  // Check if Geolocation API is supported
  const isGeolocationSupported = 'geolocation' in navigator;

  // Check permission state and iOS accuracy mode on mount
  useEffect(() => {
    if (!isGeolocationSupported) return;

    // Check if Permissions API is supported
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          setPermissionState(result.state as PermissionState);

          // Listen for permission changes
          result.addEventListener('change', () => {
            setPermissionState(result.state as PermissionState);
          });
        })
        .catch(() => {
          // Permissions API not fully supported, keep as unknown
          setPermissionState('unknown');
        });
    }

    // Check iOS location accuracy mode
    checkiOSLocationAccuracy().then(mode => {
      setIosAccuracyMode(mode);
    });
  }, [isGeolocationSupported]);

  // Handle geolocation errors
  const handleGeolocationError = useCallback((error: GeolocationPositionError) => {
    let locationError: LocationError;

    switch (error.code) {
      case error.PERMISSION_DENIED:
        locationError = {
          code: 'PERMISSION_DENIED',
          message: '位置情報の使用が拒否されました。ブラウザの設定で位置情報を許可してください。'
        };
        setPermissionState('denied');
        break;
      case error.POSITION_UNAVAILABLE:
        locationError = {
          code: 'POSITION_UNAVAILABLE',
          message: '位置情報を取得できませんでした。GPS信号が弱い可能性があります。'
        };
        break;
      case error.TIMEOUT:
        locationError = {
          code: 'TIMEOUT',
          message: '位置情報の取得がタイムアウトしました。再度お試しください。'
        };
        break;
      default:
        locationError = {
          code: 'POSITION_UNAVAILABLE',
          message: '位置情報の取得中にエラーが発生しました。'
        };
    }

    setError(locationError);
    setLocationState('error');
    onError?.(locationError);

    // Announce error to screen readers
    announceToScreenReader(locationError.message, 'assertive');
  }, [onError]);

  // Get current location with high accuracy
  const getCurrentLocation = useCallback(async () => {
    if (!isGeolocationSupported) {
      const error: LocationError = {
        code: 'NOT_SUPPORTED',
        message: 'お使いのブラウザは位置情報機能をサポートしていません。'
      };
      setError(error);
      setLocationState('error');
      onError?.(error);
      announceToScreenReader(error.message, 'assertive');
      return;
    }

    setLocationState('requesting');
    setError(null);
    setLocationProgress(null);
    setLocationResult(null);

    // Announce loading state
    announceToScreenReader('高精度位置情報を取得しています...', 'polite');

    try {
      const result = await highAccuracyLocationService.getHighAccuracyLocation({
        targetAccuracy: 15, // 15メートル以下を目標
        maxAttempts: 5,
        timeout: 30000,
        enableSmoothing: true,
        progressCallback: (progress) => {
          setLocationProgress(progress);

          // 進捗をスクリーンリーダーに通知
          if (progress.status === 'completed') {
            announceToScreenReader(progress.message, 'polite');
          }
        }
      });

      setLocationResult(result);
      setCurrentLocation(result.location);
      setLocationState('success');
      setPermissionState('granted');
      onLocationChange(result.location);

      // Announce success to screen readers
      announceToScreenReader(
        `高精度位置情報を取得しました（精度: ${Math.round(result.accuracy)}m、信頼度: ${result.confidence}）`,
        'polite'
      );

    } catch (error) {
      console.error('High accuracy location failed:', error);

      // フォールバック: 標準の位置情報取得
      try {
        const fallbackOptions = getBrowserOptimizedOptions();

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location: Location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };

            const fallbackResult: LocationResult = {
              location,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
              source: 'network',
              confidence: position.coords.accuracy <= 50 ? 'medium' : 'low'
            };

            setLocationResult(fallbackResult);
            setCurrentLocation(location);
            setLocationState('success');
            setPermissionState('granted');
            onLocationChange(location);

            announceToScreenReader(
              `位置情報を取得しました（標準精度: ${Math.round(position.coords.accuracy)}m）`,
              'polite'
            );
          },
          (fallbackError) => {
            handleGeolocationError(fallbackError);
          },
          fallbackOptions
        );
      } catch (fallbackError) {
        if (fallbackError instanceof GeolocationPositionError) {
          handleGeolocationError(fallbackError);
        } else {
          const locationError: LocationError = {
            code: 'POSITION_UNAVAILABLE',
            message: '位置情報の取得に失敗しました。'
          };
          setError(locationError);
          setLocationState('error');
          onError?.(locationError);
        }
      }
    }
  }, [isGeolocationSupported, onLocationChange, onError, handleGeolocationError]);

  // Clear location
  const clearLocation = useCallback(() => {
    setCurrentLocation(null);
    setLocationState('idle');
    setError(null);
    setLocationProgress(null);
    setLocationResult(null);
    onLocationChange(null);
    highAccuracyLocationService.clearHistory();
    announceToScreenReader('位置情報をクリアしました', 'polite');
  }, [onLocationChange]);

  // Get status message for screen readers
  const getStatusMessage = () => {
    switch (locationState) {
      case 'requesting':
        return '位置情報を取得中です...';
      case 'success':
        return '位置情報を取得しました';
      case 'error':
        return error?.message || 'エラーが発生しました';
      default:
        return '';
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (locationState) {
      case 'requesting':
        return '取得中...';
      case 'success':
        return '位置情報を再取得';
      default:
        return '現在地を取得';
    }
  };

  // Get permission guidance message
  const getPermissionGuidance = () => {
    if (permissionState === 'denied') {
      return (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            位置情報が拒否されています。ブラウザのアドレスバーの位置情報アイコンをクリックして許可してください。
          </p>
        </div>
      );
    }

    // iOS「正確な位置情報」のガイダンス
    if (iosAccuracyMode === 'reduced') {
      return (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800 font-medium mb-1">
            📍 より正確な位置情報を取得するには
          </p>
          <p className="text-sm text-blue-700">
            iOSの設定 → プライバシーとセキュリティ → 位置情報サービス → Safari → 「正確な位置情報」をオンにしてください。
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Location input section */}
      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            現在地
          </label>

          <div className="flex gap-2">
            <Button
              onClick={getCurrentLocation}
              disabled={disabled || locationState === 'requesting'}
              variant={locationState === 'success' ? 'outline' : 'primary'}
              className="flex-shrink-0"
            >
              {locationState === 'requesting' && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {getButtonText()}
            </Button>

            {currentLocation && (
              <Button
                onClick={clearLocation}
                variant="outline"
                disabled={disabled}
                aria-label="位置情報をクリア"
              >
                クリア
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Status message for screen readers */}
      <div
        id={statusId}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {getStatusMessage()}
      </div>

      {/* Location progress display */}
      {locationProgress && locationState === 'requesting' && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center mb-2">
            <svg
              className="animate-spin h-4 w-4 text-blue-600 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm font-medium text-blue-800">
              {locationProgress.message}
            </p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(locationProgress.attempt / locationProgress.maxAttempts) * 100}%` }}
            />
          </div>

          <div className="flex justify-between text-xs text-blue-600">
            <span>試行: {locationProgress.attempt}/{locationProgress.maxAttempts}</span>
            {locationProgress.currentAccuracy && (
              <span>現在精度: {Math.round(locationProgress.currentAccuracy)}m</span>
            )}
            <span>目標: {locationProgress.targetAccuracy}m</span>
          </div>
        </div>
      )}

      {/* Current location display */}
      {currentLocation && locationState === 'success' && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">
                {locationResult?.confidence === 'high' ? '高精度位置情報を取得しました' : '位置情報を取得しました'}
              </p>

              <div className="mt-1 space-y-1">
                <p className="text-xs text-green-600">
                  緯度: {currentLocation.lat.toFixed(6)}, 経度: {currentLocation.lng.toFixed(6)}
                </p>

                {locationResult && (
                  <div className="flex items-center gap-3 text-xs text-green-600">
                    <span>精度: {Math.round(locationResult.accuracy)}m</span>
                    <span>信頼度: {locationResult.confidence === 'high' ? '高' : locationResult.confidence === 'medium' ? '中' : '低'}</span>
                    <span>ソース: {locationResult.source === 'gps' ? 'GPS' : locationResult.source === 'network' ? 'ネットワーク' : 'パッシブ'}</span>
                  </div>
                )}

                {locationResult?.confidence === 'high' && (
                  <div className="flex items-center text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full w-fit">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    高精度GPS取得成功
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && locationState === 'error' && (
        <div
          id={errorId}
          className="p-3 bg-red-50 border border-red-200 rounded-md"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-red-400 mr-2 mt-0.5 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-800">
                位置情報の取得に失敗しました
              </p>
              <p className="text-sm text-red-600 mt-1">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Permission guidance */}
      {getPermissionGuidance()}

      {/* Help text */}
      <div className="text-xs text-gray-500">
        <p>
          位置情報は外出プランの提案にのみ使用され、250mグリッドに丸めて処理されます。
          個人を特定できる正確な位置情報は保存されません。
        </p>
      </div>
    </div>
  );
}